<template>
  <v-container fluid class="pa-6 fleet-overview-container">
    <!-- Loading overlay -->
    <div v-if="loading" class="loader-overlay">
      <v-card class="pa-6 text-center" elevation="8">
        <v-progress-circular indeterminate color="primary" size="64" />
        <div class="mt-4 text-h6 grey--text">Chargement des données...</div>
      </v-card>
    </div>

    <!-- Header -->
    <v-row class="mb-4">
      <v-col cols="12">
        <div class="d-flex align-center justify-space-between">
          <div>
            <h1 class="text-h4 font-weight-bold">Supervision du parc machines</h1>
            <p class="text-subtitle-1 grey--text mt-1">
              Vue d'ensemble de l'état du parc sur la période sélectionnée
            </p>
          </div>
          <v-btn color="primary" @click="fetchData" :loading="loading">
            <v-icon left>mdi-refresh</v-icon>
            Actualiser
          </v-btn>
        </div>
      </v-col>
    </v-row>

    <!-- Filters -->
    <v-card class="mb-6" elevation="2">
      <v-card-text>
        <v-row align="center">
          <v-col cols="12" md="4">
            <v-menu
              v-model="startDateMenu"
              :close-on-content-click="false"
              transition="scale-transition"
              offset-y
              min-width="auto"
            >
              <template v-slot:activator="{ on, attrs }">
                <v-text-field
                  v-model="startDate"
                  label="Date de début"
                  prepend-icon="mdi-calendar"
                  readonly
                  v-bind="attrs"
                  v-on="on"
                  outlined
                  dense
                  hide-details
                />
              </template>
              <v-date-picker
                v-model="startDate"
                @input="startDateMenu = false"
                no-title
                scrollable
              />
            </v-menu>
          </v-col>

          <v-col cols="12" md="4">
            <v-menu
              v-model="endDateMenu"
              :close-on-content-click="false"
              transition="scale-transition"
              offset-y
              min-width="auto"
            >
              <template v-slot:activator="{ on, attrs }">
                <v-text-field
                  v-model="endDate"
                  label="Date de fin"
                  prepend-icon="mdi-calendar"
                  readonly
                  v-bind="attrs"
                  v-on="on"
                  outlined
                  dense
                  hide-details
                />
              </template>
              <v-date-picker
                v-model="endDate"
                @input="endDateMenu = false"
                no-title
                scrollable
              />
            </v-menu>
          </v-col>

          <v-col cols="12" md="4">
            <v-select
              v-model="selectedTags"
              :items="availableTags"
              item-text="name"
              item-value="id"
              label="Filtrer par tags"
              prepend-icon="mdi-tag-multiple"
              multiple
              chips
              small-chips
              deletable-chips
              outlined
              dense
              hide-details
              clearable
            />
          </v-col>
        </v-row>
      </v-card-text>
    </v-card>

    <!-- KPI Cards -->
    <v-row class="mb-6">
      <v-col cols="12" sm="6" md="3">
        <v-card elevation="3" class="kpi-card">
          <v-card-text class="text-center py-6">
            <v-icon size="48" color="primary" class="mb-3">mdi-ticket-outline</v-icon>
            <div class="text-h3 font-weight-bold primary--text">{{ statistics.totalOpenTickets || 0 }}</div>
            <div class="text-subtitle-1 grey--text text--darken-1 mt-2">Tickets ouverts</div>
            <div class="text-caption grey--text">Sur la période</div>
          </v-card-text>
        </v-card>
      </v-col>

      <v-col cols="12" sm="6" md="3">
        <v-card elevation="3" class="kpi-card">
          <v-card-text class="text-center py-6">
            <v-icon size="48" color="orange" class="mb-3">mdi-alert-circle-outline</v-icon>
            <div class="text-h3 font-weight-bold orange--text">{{ totalAlerts }}</div>
            <div class="text-subtitle-1 grey--text text--darken-1 mt-2">Alertes totales</div>
            <div class="text-caption grey--text">Toutes catégories</div>
          </v-card-text>
        </v-card>
      </v-col>

      <v-col cols="12" sm="6" md="3">
        <v-card elevation="3" class="kpi-card">
          <v-card-text class="text-center py-6">
            <v-icon size="48" color="success" class="mb-3">mdi-kiosk</v-icon>
            <div class="text-h3 font-weight-bold success--text">{{ uniqueMachineCount }}</div>
            <div class="text-subtitle-1 grey--text text--darken-1 mt-2">Machines actives</div>
            <div class="text-caption grey--text">Machines uniques</div>
          </v-card-text>
        </v-card>
      </v-col>

      <v-col cols="12" sm="6" md="3">
        <v-card elevation="3" class="kpi-card">
          <v-card-text class="text-center py-6">
            <v-icon size="48" color="info" class="mb-3">mdi-file-document-outline</v-icon>
            <div class="text-h3 font-weight-bold info--text">{{ statistics.totalReports || 0 }}</div>
            <div class="text-subtitle-1 grey--text text--darken-1 mt-2">Rapports générés</div>
            <div class="text-caption grey--text">Sur la période</div>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <!-- Charts Row -->
    <v-row class="mb-6">
      <!-- Alert Bar Chart -->
      <v-col cols="12" lg="8">
        <v-card elevation="3" class="fill-height">
          <v-card-title class="text-h6">
            <v-icon left color="orange">mdi-chart-bar</v-icon>
            Synthèse des alertes par type
          </v-card-title>
          <v-card-text>
            <div class="chart-container">
              <canvas ref="alertsChart"></canvas>
            </div>
          </v-card-text>
        </v-card>
      </v-col>

      <!-- Alert Legend -->
      <v-col cols="12" lg="4">
        <v-card elevation="3" class="fill-height">
          <v-card-title class="text-h6">
            <v-icon left color="info">mdi-information-outline</v-icon>
            Détails des alertes
          </v-card-title>
          <v-card-text>
            <v-list dense>
              <v-list-item v-for="alert in alertDetails" :key="alert.key" class="px-0">
                <v-list-item-avatar size="32" :color="alert.color" class="mr-3">
                  <span class="white--text text-caption font-weight-bold">{{ alert.count }}</span>
                </v-list-item-avatar>
                <v-list-item-content>
                  <v-list-item-title class="text-body-2">{{ alert.name }}</v-list-item-title>
                  <v-list-item-subtitle class="text-caption">{{ alert.tooltip }}</v-list-item-subtitle>
                </v-list-item-content>
              </v-list-item>
            </v-list>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <!-- Top 10 Tables -->
    <v-row>
      <!-- Top 10 by Tickets -->
      <v-col cols="12" md="6">
        <v-card elevation="3">
          <v-card-title class="text-h6">
            <v-icon left color="primary">mdi-podium-gold</v-icon>
            Top 10 machines par tickets
          </v-card-title>
          <v-card-text class="pa-0">
            <v-data-table
              :headers="topMachinesHeaders"
              :items="topMachinesByTickets"
              :items-per-page="10"
              hide-default-footer
              class="elevation-0"
              dense
            >
              <template v-slot:item.rank="{ index }">
                <v-chip
                  :color="getRankColor(index)"
                  dark
                  small
                  class="font-weight-bold"
                >
                  {{ index + 1 }}
                </v-chip>
              </template>
              <template v-slot:item.ticketCount="{ item }">
                <v-chip color="primary" dark small>
                  {{ item.ticketCount }}
                </v-chip>
              </template>
              <template v-slot:no-data>
                <div class="text-center pa-4 grey--text">
                  Aucune donnée disponible
                </div>
              </template>
            </v-data-table>
          </v-card-text>
        </v-card>
      </v-col>

      <!-- Top 10 by Alerts -->
      <v-col cols="12" md="6">
        <v-card elevation="3">
          <v-card-title class="text-h6">
            <v-icon left color="orange">mdi-podium-gold</v-icon>
            Top 10 machines par alertes
          </v-card-title>
          <v-card-text class="pa-0">
            <v-data-table
              :headers="topMachinesAlertHeaders"
              :items="topMachinesByAlerts"
              :items-per-page="10"
              hide-default-footer
              class="elevation-0"
              dense
            >
              <template v-slot:item.rank="{ index }">
                <v-chip
                  :color="getRankColor(index)"
                  dark
                  small
                  class="font-weight-bold"
                >
                  {{ index + 1 }}
                </v-chip>
              </template>
              <template v-slot:item.alertCount="{ item }">
                <v-chip color="orange" dark small>
                  {{ item.alertCount }}
                </v-chip>
              </template>
              <template v-slot:no-data>
                <div class="text-center pa-4 grey--text">
                  Aucune donnée disponible
                </div>
              </template>
            </v-data-table>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <!-- Error Dialog -->
    <v-dialog v-model="errorDialog" max-width="400">
      <v-card>
        <v-card-title class="text-h6 error--text">
          <v-icon left color="error">mdi-alert-circle</v-icon>
          Erreur
        </v-card-title>
        <v-card-text>
          Une erreur est survenue lors du chargement des données. Veuillez réessayer.
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn color="primary" text @click="errorDialog = false">Fermer</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
import DashboardService from "@/services/DashboardService.js";
import { ALERT_FILTERS } from "@/constants/dashboard.constants.js";
import Chart from "chart.js/auto";

export default {
  name: "FleetOverviewView",

  data: () => ({
    // Loading state
    loading: false,
    errorDialog: false,

    // Date filters
    startDate: null,
    endDate: null,
    startDateMenu: false,
    endDateMenu: false,

    // Tag filter
    selectedTags: [],
    availableTags: [],

    // Statistics
    statistics: {},
    topMachinesByTickets: [],
    topMachinesByAlerts: [],
    alertsOverTime: [],

    // Chart instance
    alertsChart: null,

    // Table headers
    topMachinesHeaders: [
      { text: "#", value: "rank", sortable: false, width: "60px" },
      { text: "Machine ID", value: "_id" },
      { text: "Nom", value: "name" },
      { text: "Tickets", value: "ticketCount", align: "center" }
    ],
    topMachinesAlertHeaders: [
      { text: "#", value: "rank", sortable: false, width: "60px" },
      { text: "Machine ID", value: "_id" },
      { text: "Nom", value: "name" },
      { text: "Alertes", value: "alertCount", align: "center" }
    ]
  }),

  computed: {
    totalAlerts() {
      if (!this.statistics) return 0;
      return (
        (this.statistics.salesAlertCount || 0) +
        (this.statistics.tpaAlertCount || 0) +
        (this.statistics.pingAlertCount || 0) +
        (this.statistics.cancelledSalesAlertCount || 0) +
        (this.statistics.rackAlertCount || 0) +
        (this.statistics.voltageAlertCount || 0) +
        (this.statistics.motherboardFreezeAlertCount || 0) +
        (this.statistics.fuseAlertCount || 0) +
        (this.statistics.batteryAlertCount || 0)
      );
    },

    uniqueMachineCount() {
      return this.statistics.uniqueMachineCount || 0;
    },

    alertDetails() {
      return ALERT_FILTERS.map(filter => ({
        ...filter,
        count: this.getAlertCount(filter.key)
      }));
    }
  },

  watch: {
    startDate() {
      this.fetchData();
    },
    endDate() {
      this.fetchData();
    },
    selectedTags() {
      this.fetchData();
    }
  },

  async mounted() {
    this.initializeDates();
    await this.fetchData();
  },

  beforeDestroy() {
    if (this.alertsChart) {
      this.alertsChart.destroy();
    }
  },

  methods: {
    initializeDates() {
      const today = new Date();
      const lastWeek = new Date(today);
      lastWeek.setDate(lastWeek.getDate() - 7);

      this.endDate = today.toISOString().substring(0, 10);
      this.startDate = lastWeek.toISOString().substring(0, 10);
    },

    async fetchData() {
      if (!this.startDate || !this.endDate) return;

      this.loading = true;
      try {
        const data = await DashboardService.fetchFleetOverview(
          this.startDate,
          this.endDate,
          this.selectedTags
        );

        this.statistics = data.statistics || {};
        this.topMachinesByTickets = data.topMachinesByTickets || [];
        this.topMachinesByAlerts = data.topMachinesByAlerts || [];
        this.alertsOverTime = data.alertsOverTime || [];
        this.availableTags = data.availableTags || [];

        this.$nextTick(() => {
          this.renderAlertsChart();
        });
      } catch (error) {
        console.error("Error fetching fleet overview:", error);
        this.errorDialog = true;
      } finally {
        this.loading = false;
      }
    },

    getAlertCount(key) {
      if (!this.statistics) return 0;
      const countKey = key.replace("Alert", "AlertCount");
      return this.statistics[countKey] || 0;
    },

    getRankColor(index) {
      if (index === 0) return "amber darken-1";
      if (index === 1) return "grey lighten-1";
      if (index === 2) return "brown lighten-1";
      return "grey";
    },

    renderAlertsChart() {
      const ctx = this.$refs.alertsChart;
      if (!ctx) return;

      if (this.alertsChart) {
        this.alertsChart.destroy();
      }

      const labels = ALERT_FILTERS.map(f => f.name);
      const data = ALERT_FILTERS.map(f => this.getAlertCount(f.key));
      const colors = ALERT_FILTERS.map(f => this.getChartColor(f.color));

      this.alertsChart = new Chart(ctx, {
        type: "bar",
        data: {
          labels,
          datasets: [
            {
              label: "Nombre d'alertes",
              data,
              backgroundColor: colors,
              borderColor: colors.map(c => c.replace("0.8", "1")),
              borderWidth: 1,
              borderRadius: 4
            }
          ]
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          plugins: {
            legend: {
              display: false
            },
            tooltip: {
              callbacks: {
                title: (items) => {
                  const index = items[0].dataIndex;
                  return ALERT_FILTERS[index].name;
                },
                afterLabel: (item) => {
                  const index = item.dataIndex;
                  return ALERT_FILTERS[index].tooltip;
                }
              }
            }
          },
          scales: {
            y: {
              beginAtZero: true,
              ticks: {
                stepSize: 1
              }
            },
            x: {
              ticks: {
                maxRotation: 45,
                minRotation: 45
              }
            }
          }
        }
      });
    },

    getChartColor(vuetifyColor) {
      const colorMap = {
        purple: "rgba(156, 39, 176, 0.8)",
        green: "rgba(76, 175, 80, 0.8)",
        blue: "rgba(33, 150, 243, 0.8)",
        yellow: "rgba(255, 235, 59, 0.8)",
        orange: "rgba(255, 152, 0, 0.8)",
        red: "rgba(244, 67, 54, 0.8)",
        cyan: "rgba(0, 188, 212, 0.8)",
        pink: "rgba(233, 30, 99, 0.8)",
        "deep-orange": "rgba(255, 87, 34, 0.8)"
      };
      return colorMap[vuetifyColor] || "rgba(158, 158, 158, 0.8)";
    }
  }
};
</script>

<style scoped>
.fleet-overview-container {
  min-height: calc(100vh - 64px);
  background-color: #f5f5f5;
}

.loader-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(255, 255, 255, 0.9);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
}

.kpi-card {
  transition: transform 0.2s ease-in-out;
}

.kpi-card:hover {
  transform: translateY(-4px);
}

.chart-container {
  position: relative;
  height: 350px;
}
</style>
